/**
 * Ayarlar > Bildirimler > "Telefona bildirim" - Web Push acma/kapama.
 *
 * Bu cihazin durumuna gore tek bir satir gosterir (bkz. lib/push.ts
 * PushState). Hata/sonuc mesajlari satirin altinda kalir; sayfa disi
 * bildirim (toast) gerekmez.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../api/client";
import type { Settings } from "../api/types";
import {
  type PushState,
  disablePush,
  enablePush,
  getPushState,
} from "../lib/push";
import { Setting, Switch } from "./Ui";

const BUY_TIMEFRAMES = ["15m", "4h"] as const;

export function PushSetting({
  settings,
  onPatch,
  busy: saving,
}: {
  settings: Settings;
  onPatch: (body: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    tone: "ok" | "error";
    text: string;
  } | null>(null);

  const refresh = useCallback(async () => {
    try {
      setState(await getPushState());
    } catch {
      setState("unsupported");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = useCallback(
    async (action: () => Promise<string>) => {
      setBusy(true);
      setMessage(null);
      try {
        setMessage({ tone: "ok", text: await action() });
      } catch (error) {
        setMessage({
          tone: "error",
          text:
            error instanceof ApiError || error instanceof Error
              ? error.message
              : "İşlem tamamlanamadı.",
        });
      } finally {
        setBusy(false);
        await refresh();
      }
    },
    [refresh],
  );

  const hint =
    "Elimde Var coin'lerine gelen Düşüş sinyalleri ve kurduğun alarmlar, site kapalıyken de bu cihaza bildirim olarak gelsin.";

  return (
    <>
      <Setting name="Telefona bildirim" hint={hint}>
        {state === "on" ? (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await disablePush();
                return "Bu cihazda bildirimler kapatıldı.";
              })
            }
          >
            Kapat
          </button>
        ) : state === "off" ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await enablePush();
                return "Bildirimler bu cihazda açıldı. Denemek için test bildirimi gönderebilirsin.";
              })
            }
          >
            Bildirimleri Aç
          </button>
        ) : null}
      </Setting>

      {state === null ? <p className="note">Kontrol ediliyor…</p> : null}

      {state === "on" ? (
        <div className="btn-row" style={{ marginBottom: 8 }}>
          <span className="up" style={{ fontWeight: 600 }}>
            Bu cihazda açık
          </span>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const result = await api.pushTest();
                return result.sent > 0
                  ? `Test bildirimi ${result.sent} cihaza gönderildi. Birkaç saniye içinde gelmeli.`
                  : "Test bildirimi gönderilemedi; bildirimleri kapatıp yeniden açmayı dene.";
              })
            }
          >
            Test bildirimi gönder
          </button>
        </div>
      ) : null}

      {state === "on" ? (
        <BuySignalPush
          settings={settings}
          onPatch={onPatch}
          busy={saving || busy}
        />
      ) : null}

      {state === "on" ? (
        <QuickMovePush
          settings={settings}
          onPatch={onPatch}
          busy={saving || busy}
        />
      ) : null}

      {state === "needs-install" ? (
        <p className="note">
          <strong>iPhone / iPad'de</strong> bildirimler yalnızca uygulama olarak
          kurulunca çalışır: Safari'de alttaki <strong>Paylaş</strong> düğmesine
          dokun → <strong>Ana Ekrana Ekle</strong> → ana ekrandaki{" "}
          <strong>Sinyalx</strong> simgesinden aç ve buradan bildirimleri aç.
          (iOS 16.4 veya üstü gerekir.)
        </p>
      ) : null}

      {state === "denied" ? (
        <p className="note">
          Bu cihazda bildirim izni <strong>kapalı</strong>. Telefon veya
          tarayıcı ayarlarından sinyalx.com.tr için bildirimlere izin verip
          sayfayı yenile.
        </p>
      ) : null}

      {state === "unsupported" ? (
        <p className="note">
          Bu tarayıcı telefona bildirim göndermeyi desteklemiyor.
        </p>
      ) : null}

      {message ? (
        <p
          className={message.tone === "ok" ? "note up" : "note down"}
          role="status"
        >
          {message.text}
        </p>
      ) : null}
    </>
  );
}

/**
 * Hangi periyotta yeni Yukselis sinyali bildirimi gelsin. Her bar icin TEK
 * ozet gider (ör. "15M · 8 yeni Yükseliş sinyali: KAVA, XLM, ..."); yeni
 * ozet bir oncekinin yerine gecer. Sikliklar sunucunun gercek kaydindan
 * olculmustur (15m: bar basina ortalama ~26 sinyal, kaliteli ~4).
 */
function BuySignalPush({
  settings,
  onPatch,
  busy,
}: {
  settings: Settings;
  onPatch: (body: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const chosen = settings.notifications.push_buy_timeframes;
  const minQuality = settings.notifications.push_buy_min_quality;

  const toggle = (timeframe: string) => {
    const next = chosen.includes(timeframe)
      ? chosen.filter((item) => item !== timeframe)
      : BUY_TIMEFRAMES.filter(
          (item) => item === timeframe || chosen.includes(item),
        );
    onPatch({ push_buy_timeframes: next });
  };

  return (
    <>
      <div className="setting setting-stack">
        <div className="setting-text">
          <div className="setting-name">Yükseliş sinyali bildirimi</div>
          <div className="setting-hint">
            Seçtiğin periyotta her bar kapanışında yeni Yükseliş sinyallerinin
            özeti gelir.
          </div>
        </div>
        <div className="chips" style={{ margin: 0, paddingInline: 0 }}>
          {BUY_TIMEFRAMES.map((timeframe) => (
            <button
              key={timeframe}
              type="button"
              className="chip"
              aria-pressed={chosen.includes(timeframe)}
              disabled={busy}
              onClick={() => toggle(timeframe)}
            >
              {timeframe.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {chosen.length > 0 ? (
        <div className="setting setting-stack">
          <div className="setting-text">
            <div className="setting-name">Hangi sinyaller</div>
            <div className="setting-hint">
              Kaliteli: kalite puanı 6 üzerinden en az 5 olan sinyaller.
            </div>
          </div>
          <select
            className="select"
            value={minQuality >= 5 ? "5" : "0"}
            disabled={busy}
            onChange={(event) =>
              onPatch({ push_buy_min_quality: Number(event.target.value) })
            }
            aria-label="Yükseliş bildirimi kalite eşiği"
          >
            <option value="0">Tüm Yükseliş sinyalleri</option>
            <option value="5">Sadece kaliteli (5+)</option>
          </select>
        </div>
      ) : null}

      {chosen.includes("15m") ? (
        <p className="note">
          <strong>15M</strong> çok hareketli: neredeyse her 15 dakikada bir
          bildirim gelir (bar başına ortalama ~26 sinyal).{" "}
          {minQuality >= 5
            ? "Kaliteli filtresiyle bar başına ortalama ~4 sinyal kalır."
            : 'Azaltmak için "Sadece kaliteli (5+)" seçebilirsin.'}
        </p>
      ) : null}

      <p className="note">
        <strong>Elimde Var</strong> olarak işaretlediğin coin'lerin{" "}
        <strong>15M ve 4H</strong> Düşüş sinyalleri, bu ayardan bağımsız olarak
        her zaman gelir.
      </p>
    </>
  );
}

/** Baslangic secenekleri: sira + gunluk yaklasik sayi (40 gun olcumu, coin basina
 *  bekleme suresinden ONCE). */
const QM_STARTS: { value: number; label: string; perDay: number }[] = [
  { value: 1, label: "1. kez (Isınma)", perDay: 53 },
  { value: 2, label: "2. kez", perDay: 25 },
  { value: 3, label: "3. kez", perDay: 15 },
  { value: 4, label: "4. kez", perDay: 11 },
  { value: 5, label: "5. kez", perDay: 8 },
];

const QM_QUIET: {
  key: string;
  label: string;
  range: [number, number] | null;
}[] = [
  { key: "off", label: "Kapalı", range: null },
  { key: "23-7", label: "23:00 – 07:00", range: [23, 7] },
  { key: "0-8", label: "00:00 – 08:00", range: [0, 8] },
  { key: "22-8", label: "22:00 – 08:00", range: [22, 8] },
  { key: "1-9", label: "01:00 – 09:00", range: [1, 9] },
];

const pad = (h: number) => `${String(h).padStart(2, "0")}:00`;

/**
 * Hizli Hareket telefon bildirimleri (kullanici, 08.10: "kapsamli bildirim
 * ayari"). Tur secimi + suzgecler; sunucu her kullaniciya KENDI ayarina gore
 * gonderir (bkz. backend scanner/quickmove_notify.py).
 */
function QuickMovePush({
  settings,
  onPatch,
  busy,
}: {
  settings: Settings;
  onPatch: (body: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const qm = settings.notifications.quickmove;
  const patch = (body: Record<string, unknown>) =>
    onPatch({ quickmove_push: body });
  const anyOn = qm.repeat || qm.signs || qm.very_strong || qm.starts.length > 0;
  const toggleStart = (value: number) =>
    patch({
      starts: qm.starts.includes(value)
        ? qm.starts.filter((item) => item !== value)
        : [...qm.starts, value].sort((a, b) => a - b),
    });
  const quietKey =
    qm.quiet_from == null || qm.quiet_to == null
      ? "off"
      : (QM_QUIET.find(
          (item) =>
            item.range?.[0] === qm.quiet_from &&
            item.range?.[1] === qm.quiet_to,
        )?.key ?? "custom");
  const perDay =
    QM_STARTS.filter((item) => qm.starts.includes(item.value)).reduce(
      (sum, item) => sum + item.perDay,
      0,
    ) + (qm.very_strong ? 41 : 0);

  return (
    <>
      <div className="setting-group-title">Hızlı Hareket bildirimleri</div>

      <Setting
        name="2. nokta"
        hint="Başlayanlar'da bir coin 2. noktasını aldı (listede görünür oldu). Böyle coinlerin 3'te 1'i günü en çok yükselenlerde bitiriyor. Her coin için bir kez, günde ~20 tane."
      >
        <Switch
          checked={qm.signs}
          label="2. nokta bildirimi"
          onChange={(next) => patch({ signs: next })}
        />
      </Setting>

      <Setting
        name="Güç tekrarı"
        hint="Coin 2 günde 3. kez güçlü oldu ve fiyatı henüz fazla yükselmemiş. 5'te 1'i 5 saatte %10 yükseldi. Günde birkaç tane."
      >
        <Switch
          checked={qm.repeat}
          label="Güç tekrarı bildirimi"
          onChange={(next) => patch({ repeat: next })}
        />
      </Setting>

      <div className="setting setting-stack">
        <div className="setting-text">
          <div className="setting-name">Başlangıçlar</div>
          <div className="setting-hint">
            Fiyatı 15 dakikada %3 yükselen coinler. Hangi sıradakiler gelsin?
            "2. kez" = listeye ilk kez düşen; en iyi sonuç veren sıra. "1. kez
            (Isınma)" deneme: en erken haber, ama 10'da 1'i yükseliyor ve günde
            ~50 bildirim demek.
          </div>
        </div>
        <div className="chips" style={{ margin: 0, paddingInline: 0 }}>
          {QM_STARTS.map((item) => (
            <button
              key={item.value}
              type="button"
              className="chip"
              aria-pressed={qm.starts.includes(item.value)}
              disabled={busy}
              onClick={() => toggleStart(item.value)}
              title={`Günde yaklaşık ${item.perDay}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <Setting
        name="Çok güçlü"
        hint="Skor 10'u geçti ve fiyat yükseliyor. Dikkat: yön yazı tura, yarısı önce %3 düşüyor. Günde ~40 tane."
      >
        <Switch
          checked={qm.very_strong}
          label="Çok güçlü bildirimi"
          onChange={(next) => patch({ very_strong: next })}
        />
      </Setting>

      {anyOn ? (
        <>
          <Setting
            name="Hangi coinler"
            hint="Yalnızca Elimdekiler: Elimde Var listendeki coinler."
          >
            <select
              className="select"
              value={qm.scope}
              disabled={busy}
              onChange={(event) => patch({ scope: event.target.value })}
              aria-label="Hızlı Hareket bildirimi kapsamı"
            >
              <option value="all">Tüm coinler</option>
              <option value="held">Yalnızca Elimdekiler</option>
            </select>
          </Setting>

          <Setting
            name="+%40 olanları gönderme"
            hint="24 saatte zaten %40'tan fazla yükselmiş coinler. 10'da 7'si önce %5 düşüyor."
          >
            <Switch
              checked={qm.skip_risen}
              label="+%40 olanları gönderme"
              onChange={(next) => patch({ skip_risen: next })}
            />
          </Setting>

          <Setting
            name="Düşük hacimli coinleri gönderme"
            hint="İşlem hacmi küçük coinler (hacme göre ilk 80'in dışı). Daha sert ve güvenilmez oynar."
          >
            <Switch
              checked={qm.skip_illiquid}
              label="Düşük hacimli coinleri gönderme"
              onChange={(next) => patch({ skip_illiquid: next })}
            />
          </Setting>

          <Setting
            name="Aynı coin için bekle"
            hint="Bir coin için bildirim geldikten sonra o coinden bu süre boyunca yenisi gelmez."
          >
            <select
              className="select"
              value={qm.cooldown_h}
              disabled={busy}
              onChange={(event) =>
                patch({ cooldown_h: Number(event.target.value) })
              }
              aria-label="Aynı coin için bekleme süresi"
            >
              <option value={0}>Bekleme yok</option>
              {[1, 2, 4, 8, 24].map((hours) => (
                <option key={hours} value={hours}>
                  {hours} saat
                </option>
              ))}
            </select>
          </Setting>

          <Setting
            name="Sessiz saatler"
            hint="Bu saatlerde Hızlı Hareket bildirimi gelmez (Türkiye saati)."
          >
            <select
              className="select"
              value={quietKey}
              disabled={busy}
              onChange={(event) => {
                const chosen = QM_QUIET.find(
                  (item) => item.key === event.target.value,
                );
                if (!chosen) return;
                patch(
                  chosen.range
                    ? { quiet_from: chosen.range[0], quiet_to: chosen.range[1] }
                    : { clear_quiet: true },
                );
              }}
              aria-label="Sessiz saatler"
            >
              {QM_QUIET.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
              {quietKey === "custom" ? (
                <option value="custom">
                  {pad(qm.quiet_from ?? 0)} – {pad(qm.quiet_to ?? 0)}
                </option>
              ) : null}
            </select>
          </Setting>

          {perDay > 0 ? (
            <p className="note">
              Seçimlerinle tüm coinlerde günde yaklaşık{" "}
              <strong>{perDay}</strong> bildirim olabilir (bekleme süresi ve
              diğer filtrelerden önce). Azaltmak için "Yalnızca Elimdekiler" ya
              da daha uzun bekleme seçebilirsin.
            </p>
          ) : null}
        </>
      ) : null}
    </>
  );
}
